import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, ShoppingCart, ArrowLeft, Send, CheckCircle, FileText, List, Shield, Clock, Mail, LogIn, Truck, MessageCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { createOrder } from '../lib/api';
import { Toaster, toast } from 'sonner';

export const RequestPage = () => {
  const { t, language, requestList, removeFromRequest, clearRequest, isBuyer, updateQuantity } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedOrder, setSubmittedOrder] = useState(null);
  const [form, setForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    country: '',
    city: '',
    notes: '',
    destination_country: '',
    us_forwarder: '',
    budget_range: '',
    timeframe: '',
    specs: '',
    alternatives: '',
  });

  const confirmationText = language === 'es' ? {
    title: 'Gracias — hemos recibido su solicitud de cotización.',
    subtitle: 'Nuestro equipo revisará su solicitud, confirmará disponibilidad con nuestros proveedores y le enviará una cotización personalizada.',
    orderNumber: 'Número de Solicitud',
    company: 'Empresa',
    contact: 'Contacto',
    email: 'Correo',
    location: 'Ubicación',
    destination: 'País de Destino',
    items: 'Artículos Solicitados',
    totalUnits: 'Total de Unidades',
    emailSent: 'Se ha enviado un correo de confirmación a',
    availabilityNote: 'Confirmaremos disponibilidad y precios dentro de 24–48 horas hábiles.',
    backHome: 'Volver al Inicio',
    continueBrowsing: 'Continuar Navegando',
  } : {
    title: 'Thank you — your quote request has been received.',
    subtitle: 'Our team will review your request, confirm availability with our suppliers, and send you a custom quotation.',
    orderNumber: 'Request Number',
    company: 'Company',
    contact: 'Contact',
    email: 'Email',
    location: 'Location',
    destination: 'Destination Country',
    items: 'Requested Items',
    totalUnits: 'Total Units',
    emailSent: 'A confirmation email has been sent to',
    availabilityNote: 'We will confirm availability and pricing within 24–48 business hours.',
    backHome: 'Back to Home',
    continueBrowsing: 'Continue Browsing',
  };

  // Build the notes field by packing additional RFQ fields
  const buildEnrichedNotes = () => {
    const parts = [];
    if (form.destination_country) parts.push(`[Destination: ${form.destination_country}]`);
    if (form.us_forwarder) parts.push(`[US Forwarder: ${form.us_forwarder}]`);
    if (form.budget_range) parts.push(`[Budget: ${form.budget_range}]`);
    if (form.timeframe) parts.push(`[Timeframe: ${form.timeframe}]`);
    if (form.specs) parts.push(`[Specs: ${form.specs}]`);
    if (form.alternatives) parts.push(`[Alternatives: ${form.alternatives}]`);
    if (form.notes) parts.push(form.notes);
    return parts.join(' ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (requestList.length === 0) {
      toast.error(language === 'es' ? 'Agregue artículos a su lista' : 'Please add items to your request list');
      return;
    }
    setLoading(true);
    try {
      const enrichedNotes = buildEnrichedNotes();
      const response = await createOrder({
        company_name: form.company_name,
        contact_name: form.contact_name,
        email: form.email,
        country: form.country,
        city: form.city,
        notes: enrichedNotes,
        items: requestList.map(item => ({
          product_id: item.product_id,
          unique_id: item.unique_id,
          oem: item.oem,
          model: item.model,
          sale_price: item.sale_price,
          quantity: item.quantity || 1,
        })),
      });
      setSubmittedOrder({
        ...response.data,
        formData: form,
        items: requestList,
      });
      setSubmitted(true);
      clearRequest();
    } catch (err) {
      toast.error(language === 'es' ? 'Error al enviar. Intente nuevamente.' : 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Guest users: show login/register prompt
  if (!isBuyer) {
    return (
      <div className="min-h-screen pt-24 pb-16" data-testid="request-page-guest">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center py-24">
            <div className="w-20 h-20 flex items-center justify-center bg-[#00D4FF]/10 text-[#00D4FF] mx-auto mb-6">
              <LogIn className="w-10 h-10" />
            </div>
            <h1 className="text-3xl font-bold mb-4">{t('request.loginRequired')}</h1>
            <p className="text-white/60 mb-8 max-w-md mx-auto">{t('request.loginRequiredDesc')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/buyer/login" className="btn-primary flex items-center justify-center gap-2" data-testid="guest-login-btn">
                <LogIn className="w-4 h-4" />
                {t('request.loginBtn')}
              </Link>
              <Link to="/wholesale-register" className="btn-secondary flex items-center justify-center gap-2" data-testid="guest-register-btn">
                {t('request.registerBtn')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Confirmation Page
  if (submitted && submittedOrder) {
    const totalUnits = submittedOrder.items.reduce((s, i) => s + (i.quantity || 1), 0);
    return (
      <div className="min-h-screen pt-24 pb-16" data-testid="request-confirmation-page">
        <div className="max-w-4xl mx-auto px-6">
          {/* Success Header */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 flex items-center justify-center bg-[#10B981]/20 text-[#10B981] mx-auto mb-6">
              <CheckCircle className="w-12 h-12" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4" data-testid="confirmation-title">
              {confirmationText.title}
            </h1>
            <p className="text-white/60 text-lg max-w-xl mx-auto">
              {confirmationText.subtitle}
            </p>
          </div>

          {/* Order Details Card */}
          <div className="card-dark p-8 mb-8">
            {/* Request Number */}
            <div className="text-center mb-8 pb-8 border-b border-white/10">
              <div className="text-white/50 text-sm uppercase tracking-wider mb-2">{confirmationText.orderNumber}</div>
              <div className="text-3xl font-mono font-bold text-[#00D4FF]" data-testid="confirmation-order-number">
                {submittedOrder.order_number}
              </div>
            </div>

            {/* Company & Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <div className="text-white/50 text-sm mb-1">{confirmationText.company}</div>
                <div className="font-semibold">{submittedOrder.formData.company_name}</div>
              </div>
              <div>
                <div className="text-white/50 text-sm mb-1">{confirmationText.contact}</div>
                <div className="font-semibold">{submittedOrder.formData.contact_name}</div>
              </div>
              <div>
                <div className="text-white/50 text-sm mb-1">{confirmationText.email}</div>
                <div className="font-semibold">{submittedOrder.formData.email}</div>
              </div>
              <div>
                <div className="text-white/50 text-sm mb-1">{confirmationText.location}</div>
                <div className="font-semibold">{submittedOrder.formData.city}, {submittedOrder.formData.country}</div>
              </div>
              {submittedOrder.formData.destination_country && (
                <div>
                  <div className="text-white/50 text-sm mb-1">{confirmationText.destination}</div>
                  <div className="font-semibold">{submittedOrder.formData.destination_country}</div>
                </div>
              )}
            </div>

            {/* Items List — No Prices */}
            <div className="mb-8">
              <div className="text-white/50 text-sm uppercase tracking-wider mb-4">{confirmationText.items}</div>
              <div className="bg-[#050505] border border-white/10 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="p-3 text-left text-xs font-semibold text-white/60 uppercase">{language === 'es' ? 'Producto' : 'Product'}</th>
                      <th className="p-3 text-left text-xs font-semibold text-white/60 uppercase">{language === 'es' ? 'Especificaciones' : 'Specifications'}</th>
                      <th className="p-3 text-center text-xs font-semibold text-white/60 uppercase">{language === 'es' ? 'Cant.' : 'Qty'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submittedOrder.items.map((item, idx) => (
                      <tr key={idx} className="border-b border-white/5">
                        <td className="p-3">
                          <div className="font-mono text-[#00D4FF] text-xs">{item.unique_id}</div>
                          <div className="text-white text-sm">{item.oem} {item.model}</div>
                        </td>
                        <td className="p-3 text-white/60 text-xs">
                          {[item.cpu, item.ram, item.hard_drive, item.grade ? `Grade ${item.grade}` : null].filter(Boolean).join(' · ') || '—'}
                        </td>
                        <td className="p-3 text-center font-mono">{item.quantity || 1}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Total Units */}
            <div className="flex justify-between items-center pt-6 border-t border-white/10">
              <div>
                <div className="text-white/50 text-sm">{confirmationText.totalUnits}</div>
                <div className="text-2xl font-bold">{totalUnits} {language === 'es' ? 'unidades' : 'units'}</div>
              </div>
              <div className="text-right">
                <div className="text-white/40 text-sm italic">
                  {language === 'es' ? 'Cotización personalizada en camino' : 'Custom quote coming your way'}
                </div>
              </div>
            </div>
          </div>

          {/* Email Confirmation Notice */}
          <div className="glass p-6 mb-8 flex items-start gap-4">
            <Mail className="w-6 h-6 text-[#00D4FF] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white/80">
                {confirmationText.emailSent} <span className="text-[#00D4FF]">{submittedOrder.formData.email}</span>
              </p>
              <p className="text-white/50 text-sm mt-2">
                {confirmationText.availabilityNote}
              </p>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="bg-[#0A0A0A] border border-white/10 p-4 mb-8">
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center gap-2 text-sm">
                <FileText className="w-4 h-4 text-[#10B981]" />
                <span className="text-white/70">{language === 'es' ? 'Cotización detallada' : 'Detailed quotation'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <List className="w-4 h-4 text-[#10B981]" />
                <span className="text-white/70">{language === 'es' ? 'Lista de seriales disponible' : 'Serial list provided'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Shield className="w-4 h-4 text-[#10B981]" />
                <span className="text-white/70">{language === 'es' ? 'Garantía por cotización' : 'Warranty per quotation'}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/" className="btn-primary flex items-center justify-center gap-2">
              {confirmationText.backHome}
            </Link>
            <Link to="/products" className="btn-secondary flex items-center justify-center gap-2">
              {confirmationText.continueBrowsing}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Empty Request List
  if (requestList.length === 0 && !showForm) {
    return (
      <div className="min-h-screen pt-24 pb-16" data-testid="request-page-empty">
        <Toaster position="top-right" theme="dark" />
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center py-24">
            <ShoppingCart className="w-16 h-16 text-white/20 mx-auto mb-6" />
            <h1 className="text-3xl font-bold mb-4">{t('request.title')}</h1>
            <p className="text-white/60 mb-8">{t('request.empty')}</p>
            <Link to="/products" className="btn-primary">
              {t('request.continue')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const totalUnitsInCart = requestList.reduce((s, i) => s + (i.quantity || 1), 0);

  // Request List & Form
  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="request-page">
      <Toaster position="top-right" theme="dark" />
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/products" className="flex items-center gap-2 text-white/60 hover:text-white mb-4">
              <ArrowLeft className="w-4 h-4" />
              {language === 'es' ? 'Volver a Productos' : 'Back to Products'}
            </Link>
            <h1 className="text-3xl font-bold">{language === 'es' ? 'Solicitud de Cotización' : 'Quote Request'}</h1>
            <p className="text-white/50 mt-1">
              {language === 'es'
                ? 'Revise los artículos y envíe su solicitud. Nuestro equipo preparará una cotización personalizada.'
                : 'Review items and submit your request. Our team will prepare a custom quotation.'}
            </p>
          </div>
          {requestList.length > 0 && (
            <button
              onClick={clearRequest}
              className="text-white/60 hover:text-red-400 text-sm flex items-center gap-2"
              data-testid="clear-request-btn"
            >
              <Trash2 className="w-4 h-4" />
              {t('request.clear')}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Items List — No Prices */}
          <div className="lg:col-span-2">
            <div className="card-dark overflow-hidden overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#0A0A0A] border-b border-white/10">
                    <th className="p-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">{language === 'es' ? 'Producto' : 'Product'}</th>
                    <th className="p-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">{language === 'es' ? 'Especificaciones' : 'Specifications'}</th>
                    <th className="p-3 text-center text-xs font-semibold text-white/60 uppercase tracking-wider">{language === 'es' ? 'Grado' : 'Grade'}</th>
                    <th className="p-3 text-center text-xs font-semibold text-white/60 uppercase tracking-wider">{language === 'es' ? 'Cant.' : 'Qty'}</th>
                    <th className="p-3 text-center text-xs font-semibold text-white/60 uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody>
                  {requestList.map((item) => {
                    const qty = item.quantity || 1;
                    return (
                      <tr key={item.product_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`request-item-${item.product_id}`}>
                        <td className="p-3">
                          <div className="font-mono text-[#00D4FF] text-xs">{item.unique_id}</div>
                          <div className="text-white text-sm">{item.oem} {item.model}</div>
                        </td>
                        <td className="p-3 text-white/60 text-xs">
                          {[item.cpu, item.ram, item.hard_drive, item.screen].filter(Boolean).join(' · ') || '—'}
                        </td>
                        <td className="p-3 text-center">
                          {item.grade && (
                            <span className="px-2 py-0.5 text-xs font-bold bg-white/10 text-white">{item.grade}</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <input
                            type="number"
                            min="1"
                            value={qty}
                            onChange={(e) => updateQuantity(item.product_id, parseInt(e.target.value) || 1)}
                            className="w-16 px-2 py-1 bg-[#050505] border border-white/10 text-center text-white font-mono text-sm focus:border-[#00D4FF] outline-none"
                            data-testid={`qty-input-${item.product_id}`}
                          />
                        </td>
                        <td className="p-3 text-center">
                          <button
                            onClick={() => removeFromRequest(item.product_id)}
                            className="text-white/40 hover:text-red-400 p-1"
                            data-testid={`remove-item-${item.product_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Warranty & Fulfillment Notes */}
            <div className="mt-4 space-y-2">
              <div className="flex items-start gap-2 text-xs text-white/40">
                <Shield className="w-4 h-4 shrink-0 mt-0.5 text-white/30" />
                <span>{t('rfq.warrantyNote')}</span>
              </div>
              <div className="flex items-start gap-2 text-xs text-white/40">
                <Truck className="w-4 h-4 shrink-0 mt-0.5 text-white/30" />
                <span>{t('rfq.fulfillmentNote')}</span>
              </div>
            </div>
          </div>

          {/* Summary / Form */}
          <div className="lg:col-span-1">
            {!showForm ? (
              <div className="card-dark p-6 sticky top-24">
                <h3 className="text-lg font-semibold mb-6">{language === 'es' ? 'Resumen de Solicitud' : 'Request Summary'}</h3>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-white/60">
                    <span>{language === 'es' ? 'Líneas' : 'Line Items'}</span>
                    <span className="font-mono">{requestList.length}</span>
                  </div>
                  <div className="flex justify-between text-white/60">
                    <span>{language === 'es' ? 'Total Unidades' : 'Total Units'}</span>
                    <span className="font-mono">{totalUnitsInCart}</span>
                  </div>
                  <div className="border-t border-white/10 pt-3">
                    <p className="text-white/40 text-xs italic">
                      {language === 'es'
                        ? 'Los precios se proporcionarán en su cotización personalizada.'
                        : 'Pricing will be provided in your custom quotation.'}
                    </p>
                  </div>
                </div>

                {/* MOQ Note */}
                <div className="bg-[#050505] border border-white/10 p-3 mb-4 text-xs" data-testid="moq-note">
                  <p className="text-[#00D4FF]">
                    {t('rfq.moqNote')}
                  </p>
                </div>

                {/* Availability Note */}
                <div className="bg-[#10B981]/10 border border-[#10B981]/20 p-3 mb-6">
                  <p className="text-[#10B981] text-sm">
                    {language === 'es'
                      ? 'Confirmaremos disponibilidad y precios dentro de 24–48 horas hábiles.'
                      : 'We will confirm availability and pricing within 24–48 business hours.'}
                  </p>
                </div>

                <button
                  onClick={() => setShowForm(true)}
                  className="btn-primary w-full"
                  data-testid="proceed-to-form-btn"
                >
                  {language === 'es' ? 'Completar Solicitud' : 'Complete Request'}
                </button>
              </div>
            ) : (
              <div className="card-dark p-6 sticky top-24">
                <h3 className="text-lg font-semibold mb-6">{language === 'es' ? 'Datos de Contacto y Envío' : 'Contact & Shipping Details'}</h3>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="request-form">
                  <div>
                    <label className="block text-white/60 text-sm mb-2">{t('request.form.company')} *</label>
                    <input
                      type="text"
                      required
                      value={form.company_name}
                      onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                      className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                      data-testid="request-company-input"
                    />
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">{t('request.form.contact')} *</label>
                    <input
                      type="text"
                      required
                      value={form.contact_name}
                      onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                      className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                      data-testid="request-contact-input"
                    />
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">{t('request.form.email')} *</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                      data-testid="request-email-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/60 text-sm mb-2">{t('request.form.country')} *</label>
                      <input
                        type="text"
                        required
                        value={form.country}
                        onChange={(e) => setForm({ ...form, country: e.target.value })}
                        className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                        data-testid="request-country-input"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-2">{t('request.form.city')} *</label>
                      <input
                        type="text"
                        required
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                        className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                        data-testid="request-city-input"
                      />
                    </div>
                  </div>

                  {/* RFQ-Specific Fields */}
                  <div className="pt-4 border-t border-white/10">
                    <p className="text-white/40 text-xs mb-4">
                      {language === 'es' ? 'Detalles adicionales para su cotización (opcional)' : 'Additional details for your quote (optional)'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-white/60 text-sm mb-2">{t('rfq.destinationCountry')}</label>
                    <input
                      type="text"
                      value={form.destination_country}
                      onChange={(e) => setForm({ ...form, destination_country: e.target.value })}
                      placeholder={language === 'es' ? 'Ej: Colombia, República Dominicana' : 'E.g. Colombia, Dominican Republic'}
                      className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                      data-testid="request-destination-input"
                    />
                  </div>

                  <div>
                    <label className="block text-white/60 text-sm mb-2">{t('rfq.usForwarder')}</label>
                    <input
                      type="text"
                      value={form.us_forwarder}
                      onChange={(e) => setForm({ ...form, us_forwarder: e.target.value })}
                      placeholder={language === 'es' ? 'Nombre y dirección del forwarder' : 'Forwarder name & address'}
                      className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                      data-testid="request-forwarder-input"
                    />
                    <p className="text-white/30 text-xs mt-1">{t('rfq.forwarderAssist')}</p>
                  </div>

                  <div>
                    <label className="block text-white/60 text-sm mb-2">{t('rfq.budgetRange')}</label>
                    <input
                      type="text"
                      value={form.budget_range}
                      onChange={(e) => setForm({ ...form, budget_range: e.target.value })}
                      placeholder={language === 'es' ? 'Ej: $50–$80 por unidad' : 'E.g. $50–$80 per unit'}
                      className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                      data-testid="request-budget-input"
                    />
                  </div>

                  <div>
                    <label className="block text-white/60 text-sm mb-2">{t('rfq.timeframe')}</label>
                    <input
                      type="text"
                      value={form.timeframe}
                      onChange={(e) => setForm({ ...form, timeframe: e.target.value })}
                      placeholder={language === 'es' ? 'Ej: Dentro de 2 semanas' : 'E.g. Within 2 weeks'}
                      className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                      data-testid="request-timeframe-input"
                    />
                  </div>

                  <div>
                    <label className="block text-white/60 text-sm mb-2">{t('rfq.specsRequirements')}</label>
                    <textarea
                      rows={2}
                      value={form.specs}
                      onChange={(e) => setForm({ ...form, specs: e.target.value })}
                      placeholder={language === 'es' ? 'Requisitos adicionales de especificaciones' : 'Additional specification requirements'}
                      className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF] resize-none"
                      data-testid="request-specs-input"
                    />
                  </div>

                  <div>
                    <label className="block text-white/60 text-sm mb-2">{t('rfq.alternatives')}</label>
                    <input
                      type="text"
                      value={form.alternatives}
                      onChange={(e) => setForm({ ...form, alternatives: e.target.value })}
                      placeholder={language === 'es' ? 'Ej: HP ProBook también aceptable' : 'E.g. HP ProBook also acceptable'}
                      className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                      data-testid="request-alternatives-input"
                    />
                  </div>

                  <div>
                    <label className="block text-white/60 text-sm mb-2">{t('request.form.notes')}</label>
                    <textarea
                      rows={2}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF] resize-none"
                      data-testid="request-notes-input"
                    />
                  </div>

                  <div className="pt-4 border-t border-white/10">
                    <div className="flex justify-between mb-2 text-white/60">
                      <span>{language === 'es' ? 'Artículos' : 'Items'}</span>
                      <span className="font-mono">{requestList.length}</span>
                    </div>
                    <div className="flex justify-between mb-4 text-white/60">
                      <span>{language === 'es' ? 'Total Unidades' : 'Total Units'}</span>
                      <span className="font-mono">{totalUnitsInCart}</span>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary w-full flex items-center justify-center gap-2"
                      data-testid="submit-request-btn"
                    >
                      {loading ? (language === 'es' ? 'Enviando...' : 'Submitting...') : (
                        <>
                          <Send className="w-4 h-4" />
                          {language === 'es' ? 'Enviar Solicitud de Cotización' : 'Submit Quote Request'}
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="w-full mt-3 text-white/60 hover:text-white text-sm"
                    >
                      {language === 'es' ? 'Volver al Resumen' : 'Back to Summary'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
